import { Link } from '@inertiajs/react';
import axios from 'axios';
import { Facebook, Mail, MapPin, Send } from 'lucide-react';
import { useState } from 'react';
import Swal from 'sweetalert2';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

export default function LandingFooter() {
    const [email, setEmail] = useState('');
    const [subscribing, setSubscribing] = useState(false);
    const currentYear = new Date().getFullYear();

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !email.includes('@')) {
            Swal.fire('Error', 'Please enter a valid email address', 'error');
            return;
        }

        setSubscribing(true);

        try {
            const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';

            const res = await axios.post(
                '/newsletter',
                { email },
                {
                    headers: {
                        'X-CSRF-TOKEN': csrf,
                        'Content-Type': 'application/json',
                    },
                },
            );

            const decoded = res.data.encoded ? JSON.parse(atob(res.data.encoded)) : res.data;

            if (decoded.status === 1) {
                Swal.fire('Subscribed!', decoded.message, 'success');
                setEmail('');
            } else if (decoded.status === 0 && decoded.error?.email) {
                const errorMsg = Array.isArray(decoded.error.email) ? decoded.error.email[0] : decoded.error.email;
                Swal.fire('Error', errorMsg, 'error');
            } else {
                Swal.fire('Error', 'Something went wrong. Please try again.', 'error');
            }
        } catch (error: any) {
            console.error('AXIOS ERROR:', error);
            console.log('RESPONSE:', error?.response?.data);
            Swal.fire('Error', 'Failed to subscribe. Please try again.', 'error');
        } finally {
            setSubscribing(false);
        }
    };

    return (
        <footer className="relative overflow-hidden border-t border-[oklch(0.12_0_0)] bg-[oklch(0.145_0_0)] text-gray-300">
            {/* Ambient Background Light Orbs */}
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-[#00963f]/10 blur-3xl" />
            <div className="pointer-events-none absolute -top-16 -right-16 h-64 w-64 rounded-full bg-yellow-500/5 blur-3xl" />

            <div className="relative z-10 mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-12 xl:grid-cols-5">
                    <div className="lg:col-span-1">
                        <div className="group mb-6 flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center overflow-hidden transition-transform duration-300 group-hover:scale-105">
                                <img
                                    src={`${baseUrl}/images/logo.webp`}
                                    alt="Northeastern College Logo"
                                    className="h-9 w-9 object-contain"
                                    width={45}
                                    height={45}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>

                            <div>
                                <h3 className="text-base leading-tight font-bold tracking-tight text-white sm:text-lg">NORTHEASTERN COLLEGE</h3>
                                <p className="text-[10px] font-semibold tracking-widest text-[#21d36b] uppercase">True Mint of Wisdom</p>
                            </div>
                        </div>

                        <div className="space-y-4 text-sm text-gray-300">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#00963f]/10 text-[#21d36b] transition-colors hover:bg-[#00963f]/20 hover:text-[#58e792]">
                                    <MapPin className="h-4 w-4" />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold transition-colors hover:text-[#21d36b]">Villasis, Santiago City</p>
                                    <p className="text-xs text-gray-400">Isabela, 3311</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#00963f]/10 text-[#21d36b] transition-colors hover:bg-[#00963f]/20 hover:text-[#58e792]">
                                    <Facebook className="h-4 w-4" />
                                </div>
                                <a
                                    href="https://www.facebook.com/NCnianAko"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-semibold transition-colors hover:text-[#21d36b]"
                                >
                                    facebook.com/NCnianAko
                                </a>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h4 className="mb-5 text-sm font-bold tracking-wider text-white uppercase">Quick Links</h4>

                        <ul className="space-y-3 text-sm">
                            {[
                                { name: 'Collegiate Calendar', url: '/collegiate-calendar' },
                                { name: 'Career Services', url: '/careers' },
                                { name: 'Contact Us', url: '/contact' },
                                {
                                    name: 'SIAS Online',
                                    url: 'https://icloudph.com/nc/sias/',
                                    external: true,
                                },
                            ].map((item, idx) => {
                                const linkContent = (
                                    <>
                                        <span className="w-0 overflow-hidden text-[#00963f] transition-all duration-300 group-hover:w-3">→</span>
                                        <span>{item.name}</span>
                                    </>
                                );

                                return (
                                    <li key={idx}>
                                        {item.external ? (
                                            <a
                                                href={item.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="group inline-flex items-center gap-1 text-gray-300 transition-all duration-300 hover:translate-x-1.5 hover:text-[#21d36b]"
                                            >
                                                {linkContent}
                                            </a>
                                        ) : (
                                            <Link
                                                href={item.url}
                                                className="group inline-flex items-center gap-1 text-gray-300 transition-all duration-300 hover:translate-x-1.5 hover:text-[#21d36b]"
                                            >
                                                {linkContent}
                                            </Link>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    <div>
                        <h4 className="mb-5 text-sm font-bold tracking-wider text-white uppercase">Academics</h4>

                        <ul className="space-y-3 text-sm">
                            {[
                                { name: 'Course Offerings', url: '/course-offerings' },
                                { name: 'Scholarships & Grants', url: '/scholarships-and-grants' },
                                { name: 'Academic Policies', url: '/academic-policies' },
                                { name: 'Sustainability', url: '/sdg' },
                            ].map((item, idx) => (
                                <li key={idx}>
                                    <Link
                                        href={item.url}
                                        className="group inline-flex items-center gap-1 text-gray-300 transition-all duration-300 hover:translate-x-1.5 hover:text-[#21d36b]"
                                    >
                                        <span className="w-0 overflow-hidden text-[#00963f] transition-all duration-300 group-hover:w-3">→</span>
                                        <span>{item.name}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="mb-5 text-sm font-bold tracking-wider text-white uppercase">Resources</h4>

                        <ul className="space-y-3 text-sm">
                            {[
                                { name: 'Research Center', url: '/research' },
                                { name: 'Downloadable Files', url: '/downloadable-files' },
                                // { name: 'Governance', url: '/governance' },
                                // { name: 'Integrity & Whistleblowing', url: '/governance/integrity' },
                            ].map((item, idx) => (
                                <li key={idx}>
                                    <Link
                                        href={item.url}
                                        className="group inline-flex items-center gap-1 text-gray-300 transition-all duration-300 hover:translate-x-1.5 hover:text-[#21d36b]"
                                    >
                                        <span className="w-0 overflow-hidden text-[#00963f] transition-all duration-300 group-hover:w-3">→</span>
                                        <span>{item.name}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="mb-5 text-sm font-bold tracking-wider text-white uppercase">Newsletter</h4>
                        <p className="mb-4 text-xs leading-relaxed text-gray-400">
                            Subscribe to receive direct campus announcements, notices, and academic updates.
                        </p>

                        <form onSubmit={handleSubscribe} className="space-y-3">
                            <div className="relative">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    aria-label="Email address for newsletter"
                                    className="w-full rounded-full border border-[oklch(0.22_0_0)] bg-[oklch(0.18_0_0)] px-5 py-3 pr-12 text-sm text-white shadow-inner transition-all placeholder:text-gray-400 focus:border-transparent focus:ring-2 focus:ring-[#00963f]/45 focus:outline-none"
                                    disabled={subscribing}
                                />
                                <Mail className="absolute top-1/2 right-4 h-4 w-4 -translate-y-1/2 text-[#21d36b]" />
                            </div>

                            <button
                                type="submit"
                                disabled={subscribing}
                                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0A7A35] py-3 text-sm font-semibold tracking-wider text-white uppercase shadow-md transition-all duration-200 hover:bg-[#08632b] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {subscribing ? (
                                    <>
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                                        Subscribing...
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-4 w-4" />
                                        Subscribe
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            <div className="border-t border-[oklch(0.12_0_0)] bg-black/20">
                <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
                    <div className="flex flex-col items-center justify-between gap-4 text-xs text-gray-400 md:flex-row">
                        <p className="text-center md:text-left">© {currentYear} Northeastern College. All rights reserved.</p>

                        <div className="flex flex-wrap justify-center gap-6">
                            {[
                                { name: 'Privacy Policy', url: '/privacy' },
                                { name: 'Terms of Service', url: '/terms' },
                                { name: 'Cookie Policy', url: '/cookies' },
                            ].map((item, idx) => (
                                <Link key={idx} href={item.url} className="font-medium transition-colors hover:text-[#21d36b]">
                                    {item.name}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
